import React from 'react';
import { Badge } from '@/components/ui/Badge';
import { 
  Upload,
  Instagram,
  Heart,
  Eye,
  CheckCircle2,
  Circle
} from 'lucide-react';
import TiktokIcon from '@/components/ui/TiktokIcon';
import { cn } from '@/lib/utils';

const VideoCard = ({ video, isNew = false, isDownloaded = false, onSelect, isSelected, onCrossPost }) => {
  const isInstagram = video.platform === 'instagram';
  const isDevice = video.platform === 'device';

  const formatNumber = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  return (
    <div 
      className={cn(
        "rounded-2xl overflow-hidden",
        "bg-gradient-to-br from-gray-900/90 to-gray-800/90",
        "backdrop-blur-md border border-gray-800/50",
        "transition-all duration-200 transform hover:scale-[1.02]",
        "flex flex-col h-full relative cursor-pointer group"
      )}
      onClick={() => onSelect(video)}
    >
      {/* Top Bar - Glassmorphism */}
      <div className="absolute top-0 left-0 right-0 px-3 py-2
                    bg-gradient-to-r from-gray-900/80 to-gray-800/80 
                    backdrop-blur-sm border-b border-gray-700/30
                    flex items-center justify-between">
        <div className="flex gap-1.5">
          <Badge 
            className="backdrop-blur-md bg-black/30 text-white px-2.5 py-1 rounded-full flex items-center gap-1.5"
          >
            {isDevice ? (
              <Upload className="h-3 w-3" />
            ) : isInstagram ? (
              <Instagram className="h-3 w-3" />
            ) : (
              <TiktokIcon className="h-3 w-3" />
            )}
            <span className="text-xs capitalize">{video.platform}</span>
          </Badge>
        </div>
        <div className="flex items-center gap-1 text-gray-300">
          <Heart className="w-3.5 h-3.5" />
          <span className="text-xs font-medium">{formatNumber(video.metrics?.likes || 0)}</span>
        </div>
      </div>

      {/* Video thumbnail */}
      <div className="pb-[177.77%] relative"> {/* 9:16 aspect ratio */}
        <img
          src={video.cover_image_url || video.thumbnail || video.preview_url}
          alt={`Video ${video.id}`}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        {/* Dark gradient overlay for better text visibility */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
      </div>

      {/* Bottom Bar - Glassmorphism */}
      <div className="absolute bottom-0 left-0 right-0 
                    bg-gradient-to-r from-gray-900/80 to-gray-800/80 
                    backdrop-blur-sm border-t border-gray-700/30">
        <div className="px-3 py-2 flex items-center justify-between">
          {/* Metrics */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs text-gray-400">{formatNumber(video.view_count || 0)}</span>
            </div>
            {video.metrics?.likes > 0 && (
              <div className="flex items-center gap-1">
                <Heart className="w-3.5 h-3.5 text-gray-400" fill="white" />
                <span className="text-xs text-gray-400">{formatNumber(video.metrics?.likes || 0)}</span>
              </div>
            )}
          </div>
          
          {/* Duration */}
          <div className="text-xs text-gray-400">
            {(() => {
              const seconds = Math.round(video.duration || 0);
              if (seconds >= 60) {
                const minutes = Math.floor(seconds/60);
                const remainingSeconds = seconds % 60;
                return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
              }
              return `0:${seconds.toString().padStart(2, '0')}`;
            })()}
          </div>
        </div>
      </div>

      {/* Selection indicator */}
      <div className="absolute top-3 right-3 z-[5]">
        <div className={`
          w-6 h-6 rounded-full flex items-center justify-center
          ${isSelected ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' : 'bg-black/30 backdrop-blur-md border border-white/20'}
          transition-all duration-200
        `}>
          {isSelected ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <Circle className="h-4 w-4" />
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoCard; 